/**
 * §12.4 R-MUSIC-L1/L2/L3 + §12.5 music intensity layers (spine §13, AMD-4).
 *
 * The theme generator is pure: seed in, note events out, no WebAudio, no MIDI dependency.
 * The scheduler streams those events into three crossfaded layers with a 300 ms lookahead
 * and a node-pruning active set (ported from `chess-tower-defence/src/audio/Audio.js`).
 *
 *   L1 ambient pad          gain = 1 − I
 *   L2 + bass and arp       gain = 4·I·(1 − I)
 *   L3 + lead and percussion gain = I
 *
 * All three are equal-power normalized and smoothed over ~2 s (§12.5).
 */

import type { AudioGraph } from './context';
import {
  clapVoice,
  disconnectVoice,
  hatVoice,
  kickVoice,
  leadVoice,
  midiToFreq,
  padVoice,
  stopVoice,
  subVoice,
  type Voice,
} from './synth';
import { layerGains } from './mixer';

/** Natural minor: 1 2 b3 4 5 b6 b7. */
export const SCALE_MINOR = [0, 2, 3, 5, 7, 8, 10] as const;

/** Diatonic progressions by scale degree; every one lands on the tonic in bar 1. */
export const PROGRESSIONS: readonly (readonly number[])[] = [
  [0, 5, 3, 6], // i – VI – iv – VII
  [0, 3, 4, 0], // i – iv – v – i
  [0, 5, 2, 6], // i – VI – III – VII
  [0, 6, 5, 4], // i – VII – VI – v
  [0, 4, 5, 3], // i – v – VI – iv
];

/** 16 sixteenth-note slots per bar; 1 = onset. Each lands on beat 1 with a syncopation. */
export const RHYTHMS: readonly (readonly number[])[] = [
  [1, 0, 1, 0, 1, 0, 0, 1, 1, 0, 1, 0, 1, 0, 1, 0],
  [1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 1],
  [1, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 0],
  [1, 0, 1, 0, 0, 1, 1, 0, 1, 0, 0, 1, 0, 1, 0, 0],
  [1, 0, 0, 1, 1, 0, 1, 0, 1, 0, 0, 1, 1, 0, 1, 0],
];

/** §12.4 R-MUSIC-L2 / L3 tempo. */
export const MUSIC_BPM = 112;
export const MUSIC_BARS = 4;
/** A3 natural minor — low enough for the trench, high enough for the lead to read. */
export const MUSIC_ROOT_MIDI = 57;
export const PLAYS_PER_THEME = 4;
/** Scheduler lookahead, ported from the reference. */
export const LOOKAHEAD_S = 0.3;

export const TRACK_PAD = 0;
export const TRACK_LEAD = 1;
export const TRACK_BASS = 2;
export const TRACK_ARP = 3;

export interface NoteEvent {
  startSec: number;
  durSec: number;
  midi: number;
  velocity: number;
  track: number;
}

export interface Theme {
  seed: number;
  bpm: number;
  bars: number;
  rootMidi: number;
  secPerBeat: number;
  durationSec: number;
  progression: readonly number[];
  notes: NoteEvent[];
}

export interface ThemeOptions {
  bpm?: number;
  bars?: number;
  rootMidi?: number;
}

/** mulberry32 — identical constants to `core/rng.ts`, kept local so audio never shares state. */
function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return function next(): number {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pickIndex(rnd: () => number, len: number): number {
  return Math.floor(rnd() * len);
}

/** Wraps a scale degree across octaves; degree may be negative or ≥ 7. */
export function scaleNote(rootMidi: number, degree: number): number {
  const len = SCALE_MINOR.length;
  const octave = Math.floor(degree / len);
  const idx = ((degree % len) + len) % len;
  return rootMidi + octave * 12 + SCALE_MINOR[idx];
}

/**
 * Deterministic 4-bar theme. Same seed → byte-identical note list.
 * Pad = whole-note triad an octave below the lead; melody = 70% chord tone / 30% passing tone.
 */
export function generateTheme(seed: number, opts?: ThemeOptions): Theme {
  const bpm = opts && opts.bpm ? opts.bpm : MUSIC_BPM;
  const bars = opts && opts.bars ? opts.bars : MUSIC_BARS;
  const rootMidi = opts && opts.rootMidi ? opts.rootMidi : MUSIC_ROOT_MIDI;
  const rnd = mulberry32(seed);
  const progression = PROGRESSIONS[pickIndex(rnd, PROGRESSIONS.length)];
  const secPerBeat = 60 / bpm;
  const secPerBar = secPerBeat * 4;
  const secPerSixteenth = secPerBeat / 4;
  const notes: NoteEvent[] = [];

  // Pad: one full-bar triad per bar, dropped an octave so it sits below the lead.
  for (let bar = 0; bar < bars; bar++) {
    const degree = progression[bar % progression.length];
    const t = bar * secPerBar;
    for (let k = 0; k < 3; k++) {
      notes.push({
        startSec: t,
        durSec: secPerBar,
        midi: scaleNote(rootMidi - 12, degree + k * 2),
        velocity: 58,
        track: TRACK_PAD,
      });
    }
  }

  // Bass: chord root two octaves down on beats 1 and 3, with an off-beat pickup on bar ends.
  for (let bar = 0; bar < bars; bar++) {
    const degree = progression[bar % progression.length];
    const root = scaleNote(rootMidi - 24, degree);
    const t = bar * secPerBar;
    notes.push({ startSec: t, durSec: secPerBeat * 1.4, midi: root, velocity: 88, track: TRACK_BASS });
    notes.push({ startSec: t + secPerBeat * 2, durSec: secPerBeat * 1.2, midi: root, velocity: 76, track: TRACK_BASS });
    if (rnd() < 0.5) {
      notes.push({
        startSec: t + secPerBeat * 3.5,
        durSec: secPerBeat * 0.4,
        midi: scaleNote(rootMidi - 24, degree + 4),
        velocity: 70,
        track: TRACK_BASS,
      });
    }
  }

  // Arp: eighth-note chord tones, the L2 pulse.
  for (let bar = 0; bar < bars; bar++) {
    const degree = progression[bar % progression.length];
    const t = bar * secPerBar;
    for (let step = 0; step < 8; step++) {
      const tone = degree + (step % 3) * 2;
      notes.push({
        startSec: t + step * secPerBeat * 0.5,
        durSec: secPerBeat * 0.42,
        midi: scaleNote(rootMidi, tone),
        velocity: step % 2 === 0 ? 74 : 62,
        track: TRACK_ARP,
      });
    }
  }

  // Lead: sixteenth grid, one rhythm mask per bar, 70% chord tone / 30% passing tone.
  let lastDegree = 0;
  for (let bar = 0; bar < bars; bar++) {
    const rhythm = RHYTHMS[pickIndex(rnd, RHYTHMS.length)];
    const chordDegree = progression[bar % progression.length];
    const t = bar * secPerBar;
    for (let step = 0; step < 16; step++) {
      if (!rhythm[step]) continue;
      let degree: number;
      if (rnd() < 0.7) {
        degree = chordDegree + pickIndex(rnd, 3) * 2;
      } else {
        degree = lastDegree + (rnd() < 0.5 ? -1 : 1) * (1 + pickIndex(rnd, 2));
      }
      while (degree < 0) degree += 7;
      while (degree > 12) degree -= 7;
      lastDegree = degree;
      notes.push({
        startSec: t + step * secPerSixteenth,
        durSec: secPerSixteenth,
        midi: scaleNote(rootMidi + 12, degree),
        velocity: 74 + Math.floor(rnd() * 30),
        track: TRACK_LEAD,
      });
    }
  }

  notes.sort(compareNotes);
  return {
    seed: seed >>> 0,
    bpm,
    bars,
    rootMidi,
    secPerBeat,
    durationSec: bars * secPerBar,
    progression,
    notes,
  };
}

function compareNotes(a: NoteEvent, b: NoteEvent): number {
  if (a.startSec !== b.startSec) return a.startSec - b.startSec;
  if (a.track !== b.track) return a.track - b.track;
  return a.midi - b.midi;
}

/** Deterministic successor seed, so a run's theme sequence is reproducible. */
export function nextThemeSeed(seed: number): number {
  return (Math.imul(seed, 1664525) + 1013904223) >>> 0;
}

// ---------------------------------------------------------------------------
// Streaming scheduler
// ---------------------------------------------------------------------------

interface ActiveNode {
  voice: Voice;
  endAt: number;
}

/** §12.5 layer gains, driven by director intensity I. */
export class MusicDirector {
  private graph: AudioGraph | null = null;
  private layers: GainNode[] = [];
  /** AMD-37 — level trim under the equal-power crossfade. Never above 1 (§12.6). */
  private levelNode: GainNode | null = null;
  private level = 1;
  private arpFilter: BiquadFilterNode | null = null;
  private droneFilter: BiquadFilterNode | null = null;
  private active: ActiveNode[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private theme: Theme | null = null;
  private playsDone = 0;
  private nextStartTime = 0;
  private seed = 0x1a2b3c4d;
  private playing = false;
  private intensity = 0;
  private readonly gains = new Float32Array(3);
  /** Boss encounters force I ≥ 0.8 (§8.7 via §12.5). */
  private floor = 0;

  /** Total voices this system holds: exactly MUSIC_RESERVED layer voices (§12.2). */
  readonly reservedVoices = 3;

  attach(g: AudioGraph): void {
    if (this.graph === g) return;
    this.graph = g;
    const bus = g.families.music;
    // AMD-37 — one trim node below all three layers, so the wave duck and the §12.5
    // crossfade compose instead of fighting over the same AudioParam.
    const level = g.ctx.createGain();
    level.gain.value = this.level;
    level.connect(bus);
    this.levelNode = level;
    this.layers = [g.ctx.createGain(), g.ctx.createGain(), g.ctx.createGain()];
    layerGains(this.intensity, this.gains);
    for (let i = 0; i < 3; i++) {
      this.layers[i].gain.value = this.gains[i];
      this.layers[i].connect(level);
    }
    // L2 pulse: sawtooth arp through a lowpass 600 → 2000 Hz that tracks I (§12.4 R-MUSIC-L2).
    const arp = g.ctx.createBiquadFilter();
    arp.type = 'lowpass';
    arp.frequency.value = 600;
    arp.Q.value = 1.4;
    arp.connect(this.layers[1]);
    this.arpFilter = arp;
    // L1 drone shaping: 400 → 2400 Hz (§12.4 R-DRONE).
    const drone = g.ctx.createBiquadFilter();
    drone.type = 'lowpass';
    drone.frequency.value = 400;
    drone.Q.value = 0.8;
    drone.connect(this.layers[0]);
    this.droneFilter = drone;
    this.applyIntensity(0);
  }

  setSeed(seed: number): void {
    this.seed = seed >>> 0;
  }

  /** 0..1. Crossfades over ~2 s, sample-accurate on the AudioParam timeline (§12.5). */
  setIntensity(v: number): void {
    const clamped = v < 0 ? 0 : v > 1 ? 1 : v;
    this.intensity = clamped < this.floor ? this.floor : clamped;
    this.applyIntensity(0.5);
  }

  /**
   * AMD-37 — set I and ramp the layers to it linearly over `seconds`, so the crossfade
   * lands on a known instant rather than asymptotically approaching one. The wave arc
   * uses it for all four beats: 0.35 s into the onset (the swell), 1.8 s into the lull
   * (the air letting out), and the `muster` countdown's own `remaining_s` so the mix
   * arrives exactly on the wave boundary.
   */
  setIntensityOver(v: number, seconds: number): void {
    const clamped = v < 0 ? 0 : v > 1 ? 1 : v;
    this.intensity = clamped < this.floor ? this.floor : clamped;
    this.crossfadeNow(seconds > 0.02 ? seconds : 0.02);
  }

  /**
   * AMD-37 — the music sub-mix level, ramped linearly over `seconds`. Sits under the
   * §12.5 crossfade: the crossfade decides what plays, this decides how loud it is.
   */
  setLevel(v: number, seconds: number): void {
    const clamped = v < 0 ? 0 : v > 1 ? 1 : v;
    this.level = clamped;
    const node = this.levelNode;
    const g = this.graph;
    if (!node || !g) return;
    const now = g.ctx.currentTime;
    const p = node.gain;
    p.cancelScheduledValues(now);
    p.setValueAtTime(p.value, now);
    p.linearRampToValueAtTime(clamped, now + (seconds > 0.02 ? seconds : 0.02));
  }

  get currentLevel(): number {
    return this.level;
  }

  /** §12.5 — boss encounters force I ≥ 0.8. */
  setIntensityFloor(v: number): void {
    this.floor = v < 0 ? 0 : v > 1 ? 1 : v;
    if (this.intensity < this.floor) {
      this.intensity = this.floor;
      this.applyIntensity(0.5);
    }
  }

  /** AMD-4 — a phase change crossfades over exactly 2 s rather than easing. */
  crossfadeNow(seconds = 2): void {
    const g = this.graph;
    layerGains(this.intensity, this.gains);
    if (!g) return;
    const now = g.ctx.currentTime;
    for (let i = 0; i < 3; i++) {
      const p = this.layers[i].gain;
      p.cancelScheduledValues(now);
      p.setValueAtTime(p.value, now);
      p.linearRampToValueAtTime(this.gains[i], now + seconds);
    }
    this.applyFilterTracking(now, seconds);
  }

  get currentIntensity(): number {
    return this.intensity;
  }

  /** §12.5 boss floor currently in force. 0 outside an encounter. */
  get intensityFloor(): number {
    return this.floor;
  }

  /** Read-only copy for telemetry / the §12.5 ±0.05 acceptance. */
  layerGain(i: number): number {
    return this.gains[i] ?? 0;
  }

  private applyIntensity(timeConstant: number): void {
    const g = this.graph;
    layerGains(this.intensity, this.gains);
    if (!g) return;
    const now = g.ctx.currentTime;
    for (let i = 0; i < 3; i++) {
      const p = this.layers[i].gain;
      if (timeConstant > 0) p.setTargetAtTime(this.gains[i], now, timeConstant);
      else p.setValueAtTime(this.gains[i], now);
    }
    this.applyFilterTracking(now, timeConstant > 0 ? timeConstant * 4 : 0.02);
  }

  private applyFilterTracking(now: number, seconds: number): void {
    const I = this.intensity;
    if (this.arpFilter) {
      const p = this.arpFilter.frequency;
      p.cancelScheduledValues(now);
      p.setValueAtTime(p.value, now);
      p.linearRampToValueAtTime(600 + 1400 * I, now + Math.max(0.02, seconds));
    }
    if (this.droneFilter) {
      const p = this.droneFilter.frequency;
      p.cancelScheduledValues(now);
      p.setValueAtTime(p.value, now);
      p.linearRampToValueAtTime(400 + 2000 * I, now + Math.max(0.02, seconds));
    }
  }

  start(): void {
    const g = this.graph;
    if (!g || this.playing) return;
    this.playing = true;
    this.theme = generateTheme(this.seed);
    this.playsDone = 0;
    this.nextStartTime = g.ctx.currentTime + 0.2;
    this.tick();
  }

  stop(): void {
    this.playing = false;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    const g = this.graph;
    if (!g) {
      this.active.length = 0;
      return;
    }
    const now = g.ctx.currentTime;
    for (let i = 0; i < this.active.length; i++) stopVoice(g, this.active[i].voice, now, 0.15);
    this.active.length = 0;
    this.theme = null;
  }

  private readonly onTick = (): void => {
    this.tick();
  };

  private tick(): void {
    const g = this.graph;
    if (!this.playing || !g || !this.theme) return;
    this.prune(g.ctx.currentTime);
    this.schedulePass(g, this.theme, this.nextStartTime);
    this.nextStartTime += this.theme.durationSec;
    this.playsDone++;
    if (this.playsDone >= PLAYS_PER_THEME) {
      this.seed = nextThemeSeed(this.seed);
      this.theme = generateTheme(this.seed);
      this.playsDone = 0;
    }
    const msUntil = Math.max(20, (this.nextStartTime - g.ctx.currentTime - LOOKAHEAD_S) * 1000);
    this.timer = setTimeout(this.onTick, msUntil);
  }

  /** Frees nodes whose oscillators have stopped, so the graph never hits the node ceiling. */
  private prune(now: number): void {
    const list = this.active;
    let w = 0;
    for (let i = 0; i < list.length; i++) {
      const n = list[i];
      if (n.endAt <= now) {
        disconnectVoice(n.voice);
        continue;
      }
      list[w++] = n;
    }
    list.length = w;
  }

  private push(v: Voice): void {
    this.active.push({ voice: v, endAt: v.endAt });
  }

  private schedulePass(g: AudioGraph, theme: Theme, startAt: number): void {
    const notes = theme.notes;
    const arpDest: AudioNode = this.arpFilter ?? this.layers[1];
    const padDest: AudioNode = this.droneFilter ?? this.layers[0];
    for (let i = 0; i < notes.length; i++) {
      const n = notes[i];
      const t0 = startAt + n.startSec;
      const t1 = t0 + n.durSec;
      const freq = midiToFreq(n.midi);
      switch (n.track) {
        case TRACK_PAD:
          this.push(padVoice(g, padDest, t0, t1, freq, (n.velocity / 127) * 0.16));
          break;
        case TRACK_BASS:
          this.push(subVoice(g, this.layers[1], t0, t1, freq, (n.velocity / 127) * 0.2));
          break;
        case TRACK_ARP:
          this.push(leadVoice(g, arpDest, t0, t1, freq, n.velocity * 0.55, 0.1));
          break;
        default: {
          const v = leadVoice(g, this.layers[2], t0, t1, freq, n.velocity, 0.18);
          // Short delay send on the lead only — the abyss tail (AMD-4).
          const send = g.ctx.createGain();
          send.gain.value = 0.22;
          v.gain.connect(send);
          send.connect(g.delaySend);
          this.push(v);
          break;
        }
      }
    }
    // Percussion lives in L3: kick on every beat, clap on the backbeat, hats offbeat.
    const spb = theme.secPerBeat;
    const beats = Math.max(1, Math.round(theme.durationSec / spb));
    for (let b = 0; b < beats; b++) {
      const t = startAt + b * spb;
      this.push(kickVoice(g, this.layers[2], t, 0.42));
      if (b % 2 === 1) this.push(clapVoice(g, this.layers[2], t, 0.24));
      this.push(hatVoice(g, this.layers[2], t + spb * 0.5, 0.04, 0.1));
    }
  }

  dispose(): void {
    this.stop();
    for (let i = 0; i < this.layers.length; i++) {
      try {
        this.layers[i].disconnect();
      } catch {
        // Already detached.
      }
    }
    this.layers.length = 0;
    if (this.levelNode) {
      try {
        this.levelNode.disconnect();
      } catch {
        // Already detached.
      }
      this.levelNode = null;
    }
    this.arpFilter = null;
    this.droneFilter = null;
    this.graph = null;
  }
}
